import { Box, Stack, Typography } from '@mui/material'
import { ChangeEvent, FC, useCallback, useState } from 'react'
import { CORAL_100, ERROR_RED_100, ERROR_RED_500, GRAY_200, GRAY_400, GRAY_700, GRAY_900, SUCCESS_GREEN_100, SUCCESS_GREEN_500, WHITE } from 'constants/styling/theme'

import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { IntegrationDTO } from 'models/integration'
import { IntegrationLogo } from '../IntegrationLogo'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import Modal from 'components/common/Modals/Modal/Modal'
import styles from './ConnectIntegrationModal.module.sass'
import KeyIcon from '@mui/icons-material/Key'
import { useConnectIntegration } from 'dataQueries'
import { useTranslation } from 'react-i18next'

type ModalState = 'input' | 'success' | 'error'

export interface ConnectIntegrationModalProps {
  open: boolean
  integration: IntegrationDTO | null
  onClose: () => void
  onSuccess?: () => void
  workspaceId: string
}

export const ConnectIntegrationModal: FC<ConnectIntegrationModalProps> = ({
  open,
  integration,
  onClose,
  onSuccess,
  workspaceId,
}) => {
  const { t } = useTranslation(['integrations'])
  const [modalState, setModalState] = useState<ModalState>('input')
  const [apiKey, setApiKey] = useState('')
  const [apiKeyError, setApiKeyError] = useState(false)

  const connectMutation = useConnectIntegration()

  const handleApiKeyChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setApiKey(e.target.value)
    if (apiKeyError) setApiKeyError(false)
  }, [apiKeyError])

  const handleConnect = useCallback(async () => {
    if (!apiKey.trim()) {
      setApiKeyError(true)
      return
    }

    if (!integration) return

    try {
      await connectMutation.mutateAsync({
        type: integration.type,
        apiKey: apiKey.trim(),
        workspaceId,
      })
      setModalState('success')
    } catch {
      setModalState('error')
    }
  }, [apiKey, integration, connectMutation, workspaceId])

  const handleClose = useCallback(() => {
    setModalState('input')
    setApiKey('')
    setApiKeyError(false)
    onClose()
  }, [onClose])

  const handleSuccessAction = useCallback(() => {
    handleClose()
    onSuccess?.()
  }, [handleClose, onSuccess])

  const handleRetry = useCallback(() => {
    setModalState('input')
  }, [])

  if (!integration) return null

  return (
    <Modal
      isOpen={open}
      onClickOutside={handleClose}
      onClose={handleClose}
      modalContentClassName={styles.modalContent}
    >
      <Box
        sx={{
          width: '43.8rem',
          maxWidth: '100%',
          backgroundColor: WHITE,
          borderRadius: '0.8rem',
          overflow: 'hidden',
        }}
      >
        {modalState === 'input' && (
          <InputContent
            integration={integration}
            apiKey={apiKey}
            apiKeyError={apiKeyError}
            isLoading={connectMutation.isPending}
            onApiKeyChange={handleApiKeyChange}
            onCancel={handleClose}
            onConnect={handleConnect}
          />
        )}

        {modalState === 'success' && (
          <ResultContent
            type="success"
            title={t('integrations:modal.success.title')}
            message={t('integrations:modal.success.message', { name: t(integration.name) })}
            actionLabel={t('integrations:modal.success.action', { name: t(integration.name) })}
            onAction={handleSuccessAction}
          />
        )}

        {modalState === 'error' && (
          <ResultContent
            type="error"
            title={t('integrations:modal.error.title')}
            message={t('integrations:modal.error.message')}
            actionLabel={t('integrations:modal.error.action')}
            onAction={handleRetry}
          />
        )}
      </Box>
    </Modal>
  )
}

interface InputContentProps {
  integration: IntegrationDTO
  apiKey: string
  apiKeyError: boolean
  isLoading: boolean
  onApiKeyChange: (e: ChangeEvent<HTMLInputElement>) => void
  onCancel: () => void
  onConnect: () => void
}

const InputContent: FC<InputContentProps> = ({
  integration,
  apiKey,
  apiKeyError,
  isLoading,
  onApiKeyChange,
  onCancel,
  onConnect,
}) => {
  const { t } = useTranslation(['integrations'])

  return (
    <Stack>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        padding="2.4rem"
        paddingRight="5rem"
      >
        <Stack direction="row" alignItems="center" gap="0.8rem">
          <IntegrationLogo
            logoUrl={integration.logoUrl}
            name={t(integration.name)}
          />
          <Typography
            variant="text-lg"
            fontWeight={600}
            color={GRAY_900}
          >
            {t('integrations:modal.connect.title', { name: t(integration.name) })}
          </Typography>
        </Stack>
      </Stack>

      {/* Content */}
      <Stack padding="0 2.4rem" gap="1.6rem">
        {/* Description */}
        <Typography
          component="ul"
          variant="text-md"
          color={GRAY_700}
          sx={{
            margin: 0,
            paddingLeft: '2.4rem',
            '& li': {
              marginBottom: '0.4rem',
            },
          }}
        >
          <li>{t(integration.description)}</li>
        </Typography>

        {/* API Key Input */}
        <Box sx={{
          '& form': { overflow: 'hidden' },
          '& .MuiInputBase-root': { backgroundColor: 'transparent' },
          '& .MuiInputAdornment-positionStart': { marginRight: 0 },
        }}>
          <MUIInputField
            label={t('integrations:modal.connect.api_key_label')}
            placeholder={t('integrations:modal.connect.api_key_placeholder')}
            value={apiKey}
            onChange={onApiKeyChange}
            isError={apiKeyError}
            errorText={apiKeyError ? t('integrations:modal.connect.api_key_required') : null}
            startIcon={
              <Box
                sx={{
                  backgroundColor: apiKeyError ? CORAL_100 : '#FAFAFA',
                  margin: '-12px 0 -12px -1.2rem',
                  padding: '12px 1.2rem',
                  borderRight: `1px solid ${GRAY_400}`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <KeyIcon sx={{ fontSize: '2rem' }} />
              </Box>
            }
            required
          />
        </Box>

        {/* Help text */}
        <Typography
          variant="text-sm"
          fontWeight={600}
          color={GRAY_900}
        >
          {t('integrations:modal.connect.help_text')}
        </Typography>
      </Stack>

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        gap="0.8rem"
        padding="0.8rem 2.4rem"
        sx={{
          borderTop: `1px solid ${GRAY_200}`,
          marginTop: '1.6rem',
        }}
      >
        <MUIButton type="secondaryBorder" onClick={onCancel} disabled={isLoading}>
          {t('integrations:modal.connect.cancel')}
        </MUIButton>
        <MUIButton type="defaultPrimary" onClick={onConnect} isLoading={isLoading}>
          {t('integrations:modal.connect.submit')}
        </MUIButton>
      </Stack>
    </Stack>
  )
}

interface ResultContentProps {
  type: 'success' | 'error'
  title: string
  message: string
  actionLabel: string
  onAction: () => void
}

const ResultContent: FC<ResultContentProps> = ({
  type,
  title,
  message,
  actionLabel,
  onAction,
}) => {
  const isSuccess = type === 'success'
  const iconColor = isSuccess ? SUCCESS_GREEN_500 : ERROR_RED_500
  const bgColor = isSuccess ? SUCCESS_GREEN_100 : ERROR_RED_100

  return (
    <Stack alignItems="center" padding="2.4rem">
      {/* Icon */}
      <Box
        sx={{
          width: '4.8rem',
          height: '4.8rem',
          borderRadius: '50%',
          backgroundColor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.6rem',
        }}
      >
        {isSuccess ? (
          <CheckCircleOutlinedIcon sx={{ fontSize: '2.4rem', color: iconColor }} />
        ) : (
          <ErrorOutlineOutlinedIcon sx={{ fontSize: '2.4rem', color: iconColor }} />
        )}
      </Box>

      {/* Title */}
      <Typography
        variant="text-xl"
        fontWeight={600}
        color={GRAY_900}
        textAlign="center"
        sx={{ marginBottom: '0.8rem' }}
      >
        {title}
      </Typography>

      {/* Message */}
      <Typography
        variant="text-sm"
        color={GRAY_700}
        textAlign="center"
        sx={{ marginBottom: '3.2rem' }}
      >
        {message}
      </Typography>

      {/* Action button */}
      <MUIButton type="defaultPrimary" onClick={onAction}>
        {actionLabel}
      </MUIButton>
    </Stack>
  )
}
